import { DashboardStats } from '../domain/models/dashboard-stats';
import { createReducer, on } from '@ngrx/store';
import * as AdminActions from './admin.actions';
import { logoutSuccess } from '../../auth/state/auth.actions';
import { FileWithUserInfo } from '../domain/models/file/file-user-info';
import { User } from '../domain/models/user/user';
import { CompanyWithFiles } from '../domain/models/company/company-with-files';
import { UserStats } from '../domain/models/user/user-stats';
import { CompanyStats } from '../domain/models/company/company-stats';
import { CurrentBilling } from '../domain/models/billing/current-billing';

export interface AdminState {
  dashboardStats: DashboardStats | null;
  loading: boolean;
  loadingStats: boolean;
  loadingAdminStats: boolean;
  error: string | null;
  convertedFiles: {
    items: FileWithUserInfo[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
    search?: string;
    fileType?: string;
    startDate?: Date;
    endDate?: Date;
    companyIds?: string[];
  };
  chartFiles: { createdAt: string; fileType: string }[];
  users: {
    items: User[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
    search?: string;
  };
  companies: {
    items: CompanyWithFiles[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
    search?: string;
  };
  selectedUser: User | null;
  userStats: UserStats | null;
  selectedCompany: CompanyWithFiles | null;
  selectedCompanyBilling: CurrentBilling | null;
  companyStats: CompanyStats | null;
  billing?: CurrentBilling;
  downloadingStatsCsv: {
    companyStats: boolean;
    companiesStats: boolean;
    companyStatsById: { [companyId: string]: boolean };
  };
  downloadingMonthlyBillingCsv: {
    all: boolean;
    byCompany: { [companyId: string]: boolean };
  };
}

export const initialState: AdminState = {
  dashboardStats: null,
  loading: false,
  loadingStats: false,
  loadingAdminStats: false,
  error: null,
  convertedFiles: {
    items: [],
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    search: '',
    fileType: '',
    startDate: undefined,
    endDate: undefined,
    companyIds: [],
  },
  chartFiles: [],
  users: {
    items: [],
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    search: '',
  },
  companies: {
    items: [],
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    search: '',
  },
  selectedUser: null,
  userStats: null,
  selectedCompany: null,
  selectedCompanyBilling: null,
  companyStats: null,
  billing: undefined,
  downloadingStatsCsv: {
    companyStats: false,
    companiesStats: false,
    companyStatsById: {},
  },
  downloadingMonthlyBillingCsv: {
    all: false,
    byCompany: {},
  },
};

export const adminReducer = createReducer(
  initialState,
  on(logoutSuccess, () => initialState),
  on(AdminActions.loadStatsForPeriod, (state) => ({
    ...state,
    loadingStats: true,
  })),
  on(
    AdminActions.loadStatsForPeriodSuccess,
    (state, { dashboardStats: stats }) => ({
      ...state,
      dashboardStats: {
        ...state.dashboardStats,
        ...stats,
      },
      loadingStats: false,
    }),
  ),
  on(AdminActions.loadStatsForPeriodFailure, (state, { error }) => ({
    ...state,
    loadingStats: false,
    error,
  })),
  on(AdminActions.loadStatsForPeriodAdmin, (state) => ({
    ...state,
    loadingAdminStats: true,
  })),
  on(
    AdminActions.loadStatsForPeriodAdminSuccess,
    (state, { dashboardStats }) => ({
      ...state,
      dashboardStats,
      loadingAdminStats: false,
    }),
  ),
  on(AdminActions.loadStatsForPeriodAdminFailure, (state, { error }) => ({
    ...state,
    loadingAdminStats: false,
    error,
  })),
  on(
    AdminActions.loadConvertedFilesSuccess,
    (
      state,
      {
        items,
        total,
        page,
        pageSize,
        search,
        fileType,
        sortColumn,
        sortDirection,
        startDate,
        endDate,
        companyIds,
      },
    ) => ({
      ...state,
      loading: false,
      convertedFiles: {
        items,
        total,
        page,
        pageSize,
        totalPages: Math.ceil(total / pageSize),
        search,
        fileType,
        sortColumn,
        sortDirection,
        startDate,
        endDate,
        companyIds,
      },
    }),
  ),
  on(AdminActions.loadUsers, (state) => ({
    ...state,
    loading: true,
  })),
  on(
    AdminActions.loadUsersSuccess,
    (
      state,
      { items, total, page, pageSize, search, sortColumn, sortDirection },
    ) => ({
      ...state,
      loading: false,
      users: {
        items,
        total,
        page,
        pageSize,
        totalPages: Math.ceil(total / pageSize),
        search,
        sortColumn,
        sortDirection,
      },
    }),
  ),
  on(AdminActions.loadUserById, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(AdminActions.loadUserByIdSuccess, (state, { user }) => ({
    ...state,
    selectedUser: user,
    loading: false,
  })),
  on(AdminActions.loadUserByIdFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(AdminActions.loadUserStatsByPeriod, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(AdminActions.loadUserStatsByPeriodSuccess, (state, { userStats }) => ({
    ...state,
    userStats: userStats,
    loading: false,
  })),
  on(AdminActions.loadUserStatsByPeriodFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(AdminActions.loadCompanies, (state) => ({
    ...state,
    loading: true,
  })),
  on(
    AdminActions.loadCompaniesSuccess,
    (
      state,
      { items, total, page, pageSize, search, sortColumn, sortDirection },
    ) => ({
      ...state,
      loading: false,
      companies: {
        items,
        total,
        page,
        pageSize,
        totalPages: Math.ceil(total / pageSize),
        search,
        sortColumn,
        sortDirection,
      },
    }),
  ),
  on(AdminActions.loadCompanyById, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(AdminActions.loadCompanyByIdSuccess, (state, { company }) => ({
    ...state,
    selectedCompany: company,
    loading: false,
  })),
  on(AdminActions.loadCompanyByIdFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(AdminActions.loadCompanyStats, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(AdminActions.loadCompanyStatsSuccess, (state, { companyStats }) => ({
    ...state,
    companyStats: companyStats,
    loading: false,
  })),
  on(AdminActions.loadCompanyStatsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(AdminActions.loadCurrentBilling, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(AdminActions.loadCurrentBillingSuccess, (state, { billing }) => ({
    ...state,
    billing: billing,
    loading: false,
  })),
  on(AdminActions.loadCurrentBillingFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(AdminActions.loadSelectedCompanyBilling, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(
    AdminActions.loadSelectedCompanyBillingSuccess,
    (state, { selectedCompanyBilling }) => ({
      ...state,
      selectedCompanyBilling: selectedCompanyBilling,
      loading: false,
    }),
  ),
  on(AdminActions.loadSelectedCompanyBillingFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(AdminActions.downloadCompanyStatsCsv, (state) => ({
    ...state,
    downloadingStatsCsv: { ...state.downloadingStatsCsv, companyStats: true },
  })),
  on(AdminActions.downloadCompanyStatsCsvSuccess, (state) => ({
    ...state,
    downloadingStatsCsv: { ...state.downloadingStatsCsv, companyStats: false },
  })),
  on(AdminActions.downloadCompanyStatsCsvFailure, (state) => ({
    ...state,
    downloadingStatsCsv: { ...state.downloadingStatsCsv, companyStats: false },
  })),
  on(AdminActions.downloadCompaniesStatsCsv, (state) => ({
    ...state,
    downloadingStatsCsv: { ...state.downloadingStatsCsv, companiesStats: true },
  })),
  on(AdminActions.downloadCompaniesStatsCsvSuccess, (state) => ({
    ...state,
    downloadingStatsCsv: {
      ...state.downloadingStatsCsv,
      companiesStats: false,
    },
  })),
  on(AdminActions.downloadCompaniesStatsCsvFailure, (state) => ({
    ...state,
    downloadingStatsCsv: {
      ...state.downloadingStatsCsv,
      companiesStats: false,
    },
  })),
  on(AdminActions.downloadCompanyStatsCsvById, (state, { companyId }) => ({
    ...state,
    downloadingStatsCsv: {
      ...state.downloadingStatsCsv,
      companyStatsById: {
        ...state.downloadingStatsCsv.companyStatsById,
        [companyId]: true,
      },
    },
  })),
  on(
    AdminActions.downloadCompanyStatsCsvByIdSuccess,
    (state, { companyId }) => ({
      ...state,
      downloadingStatsCsv: {
        ...state.downloadingStatsCsv,
        companyStatsById: {
          ...state.downloadingStatsCsv.companyStatsById,
          [companyId]: false,
        },
      },
    }),
  ),
  on(
    AdminActions.downloadCompanyStatsCsvByIdFailure,
    (state, { companyId }) => ({
      ...state,
      downloadingStatsCsv: {
        ...state.downloadingStatsCsv,
        companyStatsById: {
          ...state.downloadingStatsCsv.companyStatsById,
          [companyId]: false,
        },
      },
    }),
  ),
  on(AdminActions.downloadMonthlyBillingCsv, (state) => ({
    ...state,
    downloadingMonthlyBillingCsv: {
      ...state.downloadingMonthlyBillingCsv,
      all: true,
    },
  })),
  on(AdminActions.downloadMonthlyBillingCsvSuccess, (state) => ({
    ...state,
    downloadingMonthlyBillingCsv: {
      ...state.downloadingMonthlyBillingCsv,
      all: false,
    },
  })),
  on(AdminActions.downloadMonthlyBillingCsvFailure, (state) => ({
    ...state,
    downloadingMonthlyBillingCsv: {
      ...state.downloadingMonthlyBillingCsv,
      all: false,
    },
  })),
  on(
    AdminActions.downloadCompanyMonthlyBillingCsvById,
    (state, { companyId }) => ({
      ...state,
      downloadingMonthlyBillingCsv: {
        ...state.downloadingMonthlyBillingCsv,
        byCompany: {
          ...state.downloadingMonthlyBillingCsv.byCompany,
          [companyId]: true,
        },
      },
    }),
  ),
  on(
    AdminActions.downloadCompanyMonthlyBillingCsvByIdSuccess,
    (state, { companyId }) => ({
      ...state,
      downloadingMonthlyBillingCsv: {
        ...state.downloadingMonthlyBillingCsv,
        byCompany: {
          ...state.downloadingMonthlyBillingCsv.byCompany,
          [companyId]: false,
        },
      },
    }),
  ),
  on(
    AdminActions.downloadCompanyMonthlyBillingCsvByIdFailure,
    (state, { companyId }) => ({
      ...state,
      downloadingMonthlyBillingCsv: {
        ...state.downloadingMonthlyBillingCsv,
        byCompany: {
          ...state.downloadingMonthlyBillingCsv.byCompany,
          [companyId]: false,
        },
      },
    }),
  ),
);
